import React, { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import API from '../../../../services/API'

export const JOURNEY_TYPES = [
  'Outpatient',
  'Admission',
  'Discharge',
  'Transfer',
  'Interhospital',
  'Repatriation',
  'Emergency',
  'Risk Assessment',
  'Other',
]
export const MOBILITY_CATEGORIES = ['Walking', 'Chair', 'Stretcher', 'Bariatric']
export const PRIORITIES = ['Routine', 'Urgent', 'ASAP', 'Emergency']

// Field layout per rule kind — which pricing inputs make sense for it.
// (First matching rule wins for charge kinds; all matching apply for uplifts.)
export const RULE_KINDS = {
  baseCharge: {
    label: 'Base charge (callout)',
    fields: ['flat'],
    help: 'Fixed charge per journey. List specific rules (e.g. stretcher) above general ones — the first match applies.',
  },
  perMile: {
    label: 'Mileage',
    fields: ['rate', 'freeUnits', 'minCharge'],
    unitLabels: { rate: 'Rate per mile', freeUnits: 'Free miles', minCharge: 'Minimum charge' },
    help: 'Charged on the routed distance — either the patient journey (pickup → destination) or the full round trip from base, depending on the mileage basis. Leave this rule out entirely if mileage is not charged.',
  },
  perHour: {
    label: 'Crew time (hourly)',
    fields: ['rate', 'freeUnits'],
    unitLabels: { rate: 'Rate per hour', freeUnits: 'Free hours' },
    help: 'Invoicing uses recorded crew time (Mobile → Handover); quotes estimate from travel time.',
  },
  waitingTime: {
    label: 'Waiting time',
    fields: ['rate', 'unitMinutes', 'freeUnits'],
    unitLabels: {
      rate: 'Rate per block',
      unitMinutes: 'Block size (mins)',
      freeUnits: 'Free minutes',
    },
    help: 'Invoicing only — charged on recorded waiting at pickup and handover beyond the free minutes.',
  },
  mobilityUplift: {
    label: 'Mobility uplift',
    fields: ['flat', 'percent'],
    help: 'Extra for stretcher/bariatric etc. Set the mobility categories it applies to below.',
  },
  escort: {
    label: 'Escort charge',
    fields: ['rate'],
    unitLabels: { rate: 'Rate per escort' },
    help: 'Charged per escort travelling with the patient.',
  },
  timeBand: {
    label: 'Out-of-hours / day uplift',
    fields: ['flat', 'percent'],
    help: 'Applies when the journey time falls in the day/time band below (e.g. 18:00–08:00 or weekends).',
  },
  cancellation: {
    label: 'Late cancellation fee',
    fields: ['flat', 'noticeHours'],
    help: 'Invoicing only — charged when a journey closes as Cancelled. With a notice period set, cancellations made earlier than that many hours before the journey are not charged; leave it blank to charge every cancellation.',
  },
  abortedJourney: {
    label: 'Aborted / no-show',
    fields: ['flat', 'percent'],
    help: 'Invoicing only — percent charges that share of the normal price; flat replaces it.',
  },
}

export const MILEAGE_BASES = [
  {
    value: 'pickupToDropoff',
    label: 'Pickup → destination',
    help: 'Charges the routed patient journey distance only.',
  },
  {
    value: 'baseToBase',
    label: 'Base to base (round trip)',
    help: 'Charges the full trip: base → pickup → destination → back to base. Suited to ad-hoc journeys. The base location needs a map point set in Site Configuration → Base Locations.',
  },
]

const emptyRule = () => ({
  kind: 'baseCharge',
  description: '',
  basis: 'pickupToDropoff',
  match: {
    journeyTypes: [],
    mobilityCategories: [],
    priorities: [],
    dayBand: 'Any',
    timeFrom: '',
    timeTo: '',
  },
  rate: '',
  unitMinutes: '',
  freeUnits: '',
  minCharge: '',
  flat: '',
  percent: '',
  noticeHours: '',
  vat: 'standard',
  nominalCode: '',
})

const RuleCard = ({ rule, index, onChange, onDelete, currency }) => {
  const kindConfig = RULE_KINDS[rule.kind] || RULE_KINDS.baseCharge
  const set = (field, value) => onChange(index, { ...rule, [field]: value })
  const setMatch = (field, value) =>
    onChange(index, { ...rule, match: { ...rule.match, [field]: value } })

  const numberField = (field, label) => (
    <Grid item xs={6} sm={3} key={field}>
      <TextField
        label={label}
        type="number"
        size="small"
        fullWidth
        value={rule[field] ?? ''}
        onChange={(e) => set(field, e.target.value)}
        inputProps={{ min: 0, step: 'any' }}
      />
    </Grid>
  )

  return (
    <Card
      variant="outlined"
      sx={{
        overflow: 'hidden',
        borderColor: 'divider',
        boxShadow: '0 1px 0 rgba(0,0,0,0.04)',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
        sx={{
          px: 1.5,
          py: 1,
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
          <Chip
            size="small"
            label={`Rule ${index + 1}`}
            color="info"
            sx={{ borderRadius: 1, fontWeight: 800 }}
          />
          <Box minWidth={0}>
            <Typography variant="subtitle2" fontWeight={800} noWrap>
              {kindConfig.label}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {rule.description || 'No line description set'}
            </Typography>
          </Box>
        </Stack>
        <Tooltip title="Remove rule">
          <IconButton onClick={() => onDelete(index)} size="small">
            <DeleteOutlineIcon />
          </IconButton>
        </Tooltip>
      </Stack>
      <Grid container spacing={1.5} sx={{ p: 1.5 }}>
        <Grid item xs={12} sm={5}>
          <TextField
            select
            label="Charge type"
            size="small"
            fullWidth
            value={rule.kind}
            onChange={(e) => set('kind', e.target.value)}
          >
            {Object.entries(RULE_KINDS).map(([kind, config]) => (
              <MenuItem key={kind} value={kind}>
                {config.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={7}>
          <TextField
            label="Description (shows on quote/invoice lines)"
            size="small"
            fullWidth
            value={rule.description}
            onChange={(e) => set('description', e.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">
            {kindConfig.help}
          </Typography>
        </Grid>

        {rule.kind === 'perMile' && (
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Mileage basis"
              size="small"
              fullWidth
              value={rule.basis || 'pickupToDropoff'}
              onChange={(e) => set('basis', e.target.value)}
              helperText={
                MILEAGE_BASES.find(
                  (basis) => basis.value === (rule.basis || 'pickupToDropoff'),
                )?.help
              }
            >
              {MILEAGE_BASES.map((basis) => (
                <MenuItem key={basis.value} value={basis.value}>
                  {basis.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}

        {kindConfig.fields.includes('flat') &&
          numberField('flat', `Flat amount (${currency})`)}
        {kindConfig.fields.includes('rate') &&
          numberField('rate', kindConfig.unitLabels?.rate || `Rate (${currency})`)}
        {kindConfig.fields.includes('unitMinutes') &&
          numberField('unitMinutes', kindConfig.unitLabels?.unitMinutes)}
        {kindConfig.fields.includes('freeUnits') &&
          numberField('freeUnits', kindConfig.unitLabels?.freeUnits || 'Free units')}
        {kindConfig.fields.includes('minCharge') &&
          numberField('minCharge', kindConfig.unitLabels?.minCharge)}
        {kindConfig.fields.includes('percent') &&
          numberField('percent', 'Percent (%)')}
        {kindConfig.fields.includes('noticeHours') &&
          numberField('noticeHours', 'Notice period (hours)')}
        <Grid item xs={6} sm={3}>
          <TextField
            select
            label="VAT"
            size="small"
            fullWidth
            value={rule.vat}
            onChange={(e) => set('vat', e.target.value)}
          >
            <MenuItem value="standard">Standard</MenuItem>
            <MenuItem value="reduced">Reduced</MenuItem>
            <MenuItem value="none">None</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="Nominal code"
            size="small"
            fullWidth
            value={rule.nominalCode}
            onChange={(e) => set('nominalCode', e.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider>
            <Typography variant="caption" color="text.secondary">
              Applies to (leave empty for all)
            </Typography>
          </Divider>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            multiple
            size="small"
            options={JOURNEY_TYPES}
            value={rule.match.journeyTypes || []}
            onChange={(e, value) => setMatch('journeyTypes', value)}
            renderInput={(params) => (
              <TextField {...params} label="Journey types" />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            multiple
            size="small"
            options={MOBILITY_CATEGORIES}
            value={rule.match.mobilityCategories || []}
            onChange={(e, value) => setMatch('mobilityCategories', value)}
            renderInput={(params) => <TextField {...params} label="Mobility" />}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            multiple
            size="small"
            options={PRIORITIES}
            value={rule.match.priorities || []}
            onChange={(e, value) => setMatch('priorities', value)}
            renderInput={(params) => (
              <TextField {...params} label="Priorities" />
            )}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            select
            label="Days"
            size="small"
            fullWidth
            value={rule.match.dayBand || 'Any'}
            onChange={(e) => setMatch('dayBand', e.target.value)}
          >
            {['Any', 'Weekday', 'Weekend'].map((band) => (
              <MenuItem key={band} value={band}>
                {band}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="From"
            type="time"
            size="small"
            fullWidth
            value={rule.match.timeFrom || ''}
            onChange={(e) => setMatch('timeFrom', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={4}>
          <TextField
            label="To"
            type="time"
            size="small"
            fullWidth
            value={rule.match.timeTo || ''}
            onChange={(e) => setMatch('timeTo', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
      </Grid>
    </Card>
  )
}

const TariffEditorDialog = ({ open, tariff, currency = '£', onClose, onSaved }) => {
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!open) return
    setError('')
    setForm({
      name: tariff?.name || '',
      description: tariff?.description || '',
      active: tariff?.active !== false,
      isDefault: !!tariff?.isDefault,
      rules: (tariff?.rules || []).map((rule) => ({
        ...emptyRule(),
        ...rule,
        match: { ...emptyRule().match, ...(rule.match || {}) },
      })),
    })
  }, [open, tariff])

  if (!form) return null

  const setRule = (index, nextRule) =>
    setForm((prev) => ({
      ...prev,
      rules: prev.rules.map((rule, i) => (i === index ? nextRule : rule)),
    }))

  const deleteRule = (index) =>
    setForm((prev) => ({
      ...prev,
      rules: prev.rules.filter((rule, i) => i !== index),
    }))

  const addRule = () =>
    setForm((prev) => ({
      ...prev,
      rules: [...prev.rules, emptyRule()],
    }))

  const save = async () => {
    if (!form.name.trim()) {
      setError('Give the tariff a name.')
      return
    }
    setSaving(true)
    setError('')
    try {
      const payload = { ...form }
      const res = tariff?._id
        ? await API.put(`/pts/tariffs/${tariff._id}`, payload)
        : await API.post('/pts/tariffs', payload)
      if ([200, 201].includes(res.status)) {
        // Updates don't change the default flag — promote separately
        if (tariff?._id && form.isDefault && !tariff.isDefault) {
          const defaultRes = await API.post(
            `/pts/tariffs/${tariff._id}/set-default`,
          )
          if (defaultRes.status !== 200) {
            setError(
              defaultRes?.data?.error ||
                'Saved the tariff, but could not make it the default.',
            )
            return
          }
        }
        onSaved?.()
        onClose?.()
      } else {
        setError(res.data?.error || 'Unable to save the tariff.')
      }
    } catch (e) {
      // validateStatus means only network failures land here - no response body
      setError('Unable to save the tariff. Please try again later.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={saving ? null : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 800 }}>
        {tariff?._id ? `Edit tariff — ${tariff.name}` : 'New tariff'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Tariff name"
                size="small"
                fullWidth
                required
                value={form.name}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Description"
                size="small"
                fullWidth
                value={form.description}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, description: e.target.value }))
                }
              />
            </Grid>
            {!form.active && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  This tariff is archived and is kept for historical reference.
                  It cannot be used for future bookings.
                </Alert>
              </Grid>
            )}
            {form.active && !tariff?.isDefault && (
              <Grid item xs={12}>
                <Stack direction="row" spacing={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.isDefault}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            isDefault: e.target.checked,
                          }))
                        }
                      />
                    }
                    label="Make default tariff"
                  />
                </Stack>
              </Grid>
            )}
          </Grid>

          <Divider />
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="subtitle1" fontWeight={800}>
                Rules
              </Typography>
              <Typography variant="caption" color="text.secondary">
                For each charge type the first matching rule applies, so put
                specific rules above general ones. Uplifts stack.
              </Typography>
            </Box>
            <Button
              startIcon={<AddIcon />}
              size="small"
              variant="outlined"
              onClick={addRule}
            >
              Add Rule
            </Button>
          </Stack>

          {form.rules.length === 0 && (
            <Alert severity="info">
              No rules yet — add a base charge and a mileage rate to get
              started.
            </Alert>
          )}
          {form.rules.map((rule, index) => (
            <RuleCard
              key={index}
              rule={rule}
              index={index}
              currency={currency}
              onChange={setRule}
              onDelete={deleteRule}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={addRule}
          disabled={saving}
        >
          Add Rule
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} color="inherit" disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save Tariff'}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  )
}

export default TariffEditorDialog
